import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Heart, Users, Shield, Target, Eye, Sparkles, CheckCircle2, ArrowRight, Building2, Handshake } from 'lucide-react';
import { Button } from '../components/ui/button';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const valueIcons = [Eye, Shield, Heart, Users, Building2];

export const AboutPage = () => {
  const { t } = useTranslation();
  const services = t('about.services', { returnObjects: true });
  const coreValues = t('about.coreValues', { returnObjects: true });

  return (
    <div className="overflow-hidden" data-testid="about-page">
      <section className="gradient-hero py-20 lg:py-32" data-testid="about-hero">
        <div className="container-custom">
          <motion.div initial="initial" animate="animate" variants={fadeInUp} className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-tight mb-6">
              {t('about.heroTitlePre')} <span className="text-gradient">{t('about.heroTitleHighlight')}</span>
            </h1>
            <p className="text-xl md:text-2xl text-sky-600 font-medium mb-6">{t('about.heroTagline')}</p>
            <p className="text-lg text-slate-600 mb-8">{t('about.heroDesc')}</p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 lg:py-20 bg-white border-b border-slate-100" data-testid="about-founder">
        <div className="container-custom">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="grid md:grid-cols-[minmax(0,300px)_1fr] gap-8 lg:gap-12 items-center bg-slate-50 rounded-3xl p-6 sm:p-10"
            >
              <div className="relative mx-auto md:mx-0 w-56 sm:w-64 md:w-full">
                <div className="absolute -inset-3 bg-gradient-to-tr from-sky-500/20 to-sky-300/10 rounded-3xl blur-xl" aria-hidden="true" />
                <img
                  src="/orlando-founder.webp"
                  alt={t('about.founderName')}
                  loading="lazy"
                  className="relative w-full aspect-[3/4] object-cover object-top rounded-2xl shadow-xl ring-1 ring-slate-200"
                  data-testid="founder-photo"
                />
              </div>
              <div>
                <span className="inline-block text-xs font-semibold tracking-wider uppercase text-sky-600 bg-sky-50 px-3 py-1 rounded-full mb-4">
                  {t('about.founderRole')}
                </span>
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2" data-testid="founder-name">
                  {t('about.founderName')}
                </h2>
                <p className="text-lg text-slate-600 leading-relaxed" data-testid="founder-bio">
                  {t('about.founderBio')}
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="about-approach">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('about.approachTitle')}</h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">{t('about.approachP1')}</p>
                <p className="mb-6">{t('about.approachP2')}</p>
                <p>{t('about.approachP3')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="about-services">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('about.whatTitle')}</h2>
              <div className="prose prose-lg max-w-none text-slate-600 mb-10">
                <p>{t('about.whatIntro')}</p>
              </div>
              <p className="text-slate-700 font-medium mb-6">{t('about.servicesLabel')}</p>
              <div className="grid md:grid-cols-2 gap-6">
                {services.map((service, index) => (
                  <motion.div key={service.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white p-6 rounded-xl border border-slate-100 card-shadow">
                    <CheckCircle2 className="h-8 w-8 text-sky-500 mb-4" />
                    <h4 className="font-semibold text-slate-900 mb-2">{service.title}</h4>
                    <p className="text-slate-600 text-sm">{service.description}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="about-partnerships">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <div className="flex items-center gap-3 mb-6">
                <Handshake className="h-10 w-10 text-sky-500" />
                <h2 className="text-3xl md:text-4xl font-bold text-slate-900">{t('about.partnershipsTitle')}</h2>
              </div>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">{t('about.partnershipsP1')}</p>
                <p className="mb-6">{t('about.partnershipsP2')}</p>
                <p>{t('about.partnershipsP3')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing gradient-subtle" data-testid="about-impact">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-6">{t('about.impactTitle')}</h2>
              <div className="prose prose-lg max-w-none text-slate-600">
                <p className="mb-6">{t('about.impactP1')}</p>
                <p>{t('about.impactP2')}</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="section-spacing bg-white" data-testid="about-values">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">{t('about.mvTitle')}</h2>
            </motion.div>
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <motion.div initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-slate-900 p-8 rounded-2xl text-white">
                <Target className="h-10 w-10 text-sky-400 mb-4" />
                <h3 className="text-2xl font-bold mb-4">{t('about.missionTitle')}</h3>
                <p className="text-slate-300 leading-relaxed">{t('about.missionText')}</p>
              </motion.div>
              <motion.div initial={{ opacity: 0, x: 20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="bg-sky-500 p-8 rounded-2xl text-white">
                <Sparkles className="h-10 w-10 text-white mb-4" />
                <h3 className="text-2xl font-bold mb-4">{t('about.visionTitle')}</h3>
                <p className="text-sky-100 leading-relaxed">{t('about.visionText')}</p>
              </motion.div>
            </div>
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h3 className="text-2xl font-bold text-slate-900 text-center mb-8">{t('about.coreValuesTitle')}</h3>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {coreValues.map((value, index) => {
                  const Icon = valueIcons[index] || Eye;
                  return (
                    <motion.div key={value.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-slate-50 p-6 rounded-xl">
                      <Icon className="h-8 w-8 text-sky-500 mb-4" />
                      <h4 className="text-lg font-semibold text-slate-900 mb-2">{value.title}</h4>
                      <p className="text-slate-600 text-sm">{value.description}</p>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-slate-900" data-testid="about-cta">
        <div className="container-custom text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('about.ctaTitle')}</h2>
            <p className="text-slate-300 text-lg mb-8 max-w-2xl mx-auto">{t('about.ctaText')}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button className="bg-sky-500 hover:bg-sky-600 text-white h-12 px-8 rounded-full font-semibold">
                  {t('about.ctaContact')}
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/community-organizations">
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 h-12 px-8 rounded-full">{t('about.ctaPartner')}</Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
